/**
 * Rollout state machine — backend-driven.
 *
 * The controller persists every rollout in the `rollouts` table and
 * advances per-pool phases in a server-side reconciler. This module is
 * the React Query veneer the WebUI uses to start, observe, and abort
 * rollouts.
 *
 * Two scopes:
 *   - "service" — cascade the service's draft workload to every pool
 *     under it (the controller atomically promotes draft -> live as
 *     part of starting the rollout).
 *   - "pool"    — re-roll just one pool's replicas with the current
 *     intended spec (target == source, no spec change).
 *
 * Phases per pool, in order:
 *   1. queued       waiting for a slot (max_concurrent_pools)
 *   2. preflight    sanity check
 *   3. provisioning bring up green replicas (gated on observed READY)
 *   4. health-grace wait for green to settle past initial readiness
 *   5. flipping     swap Envoy routes blue → green
 *   6. draining     wait for blue connections to drain
 *   7. teardown     kill blue replicas
 *   8. complete     done
 *
 * Failure jumps to "failed" and aborts the parent rollout; the controller
 * leaves green replicas alive so the operator can investigate.
 */
import { useCallback, useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  BlueGreenStrategy,
  ReplicaStatus,
  RolloutOut,
  RolloutPoolPhase,
  RolloutPoolProgressOut,
  RolloutReplicaProgressOut,
  RolloutReplicaRole,
  RolloutScope,
  RolloutState,
  WorkloadSpec,
} from "@/api/types";

// Re-export so existing call sites can keep importing types from
// `@/api/local/rollout` (rolling out a name change is purely cosmetic).
export type {
  BlueGreenStrategy,
  ReplicaStatus,
  RolloutOut,
  RolloutPoolPhase,
  RolloutPoolProgressOut,
  RolloutReplicaProgressOut,
  RolloutReplicaRole,
  RolloutScope,
  RolloutState,
};

// ---- legacy aliases ------------------------------------------------------

/** Pool-progress alias matching the old in-browser shape. */
export type PoolPhase = RolloutPoolPhase;
export type PoolRolloutPlan = RolloutPoolProgressOut & {
  /** Wall-clock estimate, computed locally from the strategy. */
  estimated_seconds?: number;
};

/** Type alias — same shape on the wire as the legacy `ActiveRollout`. */
export type ActiveRollout = RolloutOut & { plan: RolloutPoolProgressOut[] };

/**
 * Persisted rollout history record. Same on-the-wire shape as `RolloutOut`
 * (just renamed for the UI's "history view" terminology).
 */
export type RolloutRecord = RolloutOut;

// ---- defaults ------------------------------------------------------------

export const DEFAULT_STRATEGY: BlueGreenStrategy = {
  surge_pct: 100,
  drain_seconds: 30,
  health_check_grace_seconds: 30,
  max_concurrent_pools: 1,
};

export const POOL_PHASE_LABELS: Record<RolloutPoolPhase, string> = {
  queued: "queued",
  preflight: "preflight",
  provisioning: "provisioning green",
  "health-grace": "health grace",
  flipping: "flipping traffic",
  draining: "draining blue",
  teardown: "teardown blue",
  complete: "complete",
  failed: "failed",
};

/**
 * Linear ordering of pool phases used by the WebUI's step indicator.
 * `failed` is intentionally absent — it's a terminal divergence rather
 * than a step on the path. Renderers should special-case it.
 */
export const POOL_PHASES: PoolPhase[] = [
  "queued",
  "preflight",
  "provisioning",
  "health-grace",
  "flipping",
  "draining",
  "teardown",
  "complete",
];

/**
 * Where step `step` sits relative to the pool's `current` phase, used
 * to drive the `rollout-step--*` CSS modifier on the 8-dot strip.
 *
 * `failed` collapses everything beyond queued into "pending" so the
 * indicator visibly stalls on the failure point rather than implying
 * we made progress past it.
 */
export function poolPhaseStepState(
  current: PoolPhase,
  step: PoolPhase,
): "done" | "active" | "pending" {
  const ci = POOL_PHASES.indexOf(current);
  const si = POOL_PHASES.indexOf(step);
  if (ci < 0 || si < 0) return "pending";
  if (current === "failed") return si === 0 ? "done" : "pending";
  if (si < ci) return "done";
  if (si === ci) return current === "complete" ? "done" : "active";
  return "pending";
}

export function isTerminalPhase(p: RolloutPoolPhase): boolean {
  return p === "complete" || p === "failed";
}

// ---- queries -------------------------------------------------------------

const ACTIVE_KEY = ["rollouts", "active"] as const;
const SERVICE_HISTORY_PREFIX = ["rollouts", "service"] as const;

function asActiveRollout(row: RolloutOut | null): ActiveRollout | null {
  if (!row) return null;
  // The wire-side `pool_progress` is what the legacy UI called `plan`.
  // Aliasing here keeps RolloutModal's existing field reads (`active.plan`)
  // working without touching every line.
  return { ...row, plan: row.pool_progress };
}

/**
 * Subscribe to the in-flight rollout (if any). Polls every 1s while
 * something is running so the progress bars animate in lockstep with
 * the controller's state machine.
 */
export function useActiveRolloutQuery(): UseQueryResult<ActiveRollout | null> {
  return useQuery({
    queryKey: ACTIVE_KEY,
    queryFn: async () => {
      const row = await api.get<RolloutOut | null>("/rollouts/active");
      return asActiveRollout(row);
    },
    // Poll faster while a rollout is running so progress feels live.
    // When nothing is active we still poll (just slowly) so a
    // teammate-started rollout gets picked up — returning `false` here
    // would freeze the query forever once it first sees a null/settled
    // rollout.
    refetchInterval: (q) => {
      const data = q.state.data;
      return data && data.state !== "completed" && data.state !== "aborted"
        ? 1000
        : 15_000;
    },
  });
}

/** Newest-first rollout history for one service. */
export function useServiceRolloutHistory(serviceId: number) {
  return useQuery({
    queryKey: [...SERVICE_HISTORY_PREFIX, serviceId],
    queryFn: () => api.get<RolloutOut[]>(`/services/${serviceId}/rollouts`),
    enabled: Number.isFinite(serviceId),
    staleTime: 10_000,
  });
}

const GLOBAL_HISTORY_KEY = ["rollouts", "history"] as const;

/**
 * Newest-first rollout history across every service (capped at 200).
 *
 * Drives the global Rollouts tab. Polls every 5s while an active
 * rollout is in flight (the active query already polls at 1Hz, so the
 * history just needs to be loosely fresh).
 */
export function useRolloutHistory() {
  const active = useActiveRolloutQuery();
  const isActive =
    !!active.data &&
    active.data.state !== "completed" &&
    active.data.state !== "aborted";
  return useQuery({
    queryKey: GLOBAL_HISTORY_KEY,
    queryFn: () => api.get<RolloutOut[]>("/rollouts"),
    refetchInterval: isActive ? 5_000 : false,
    staleTime: 10_000,
  });
}

/**
 * Convenience hook that returns a `RolloutDoc`-shaped view, matching the
 * legacy `useRollouts()` API used by RolloutModal and the tree badges.
 */
export interface RolloutDoc {
  active: ActiveRollout | null;
  /** Hash of what's currently running on each pool (per the latest rollout). */
  poolLiveHash: Record<number, string>;
  history: RolloutRecord[];
}

export function useRollouts(): RolloutDoc {
  const active = useActiveRolloutQuery();
  const history = useRolloutHistory();
  return useMemo<RolloutDoc>(() => {
    const a = active.data ?? null;
    const records = history.data ?? [];
    const poolLiveHash: Record<number, string> = {};
    // Walk newest-first; first completed rollout per pool wins, since
    // the ordering of `records` is already started_at DESC.
    for (const r of records) {
      if (r.state !== "completed") continue;
      for (const p of r.pool_progress) {
        if (poolLiveHash[p.pool_id] === undefined) {
          poolLiveHash[p.pool_id] = r.target_hash;
        }
      }
    }
    // Active rollout's pools are about to land on `target_hash`; show
    // that to the tree so the badge anticipates the flip.
    if (a && a.target_hash) {
      for (const p of a.pool_progress) {
        poolLiveHash[p.pool_id] = a.target_hash;
      }
    }
    return { active: a, poolLiveHash, history: records };
  }, [active.data, history.data]);
}

// ---- mutations -----------------------------------------------------------

interface StartParams {
  scope: RolloutScope;
  service_id: number;
  pool_id?: number;
  strategy?: BlueGreenStrategy;
  /**
   * Fired after the controller acks the rollout start. Used by callers
   * that need to navigate or invalidate caches once we know the rollout
   * row exists. The controller has already promoted draft -> live and
   * flipped a draft service to active by the time this runs.
   */
  on_started?: (rollout: RolloutOut) => void | Promise<void>;
  /**
   * Fired when the active-rollout query observes the rollout reaching a
   * terminal state (`completed`). Replaces the legacy "post-deploy"
   * step where the WebUI flipped enabled=true; the controller now does
   * that as part of `start_rollout`, so this is purely a navigation /
   * toast hook.
   */
  on_complete?: (rollout: RolloutOut) => void | Promise<void>;
}

/**
 * Hook variant of the legacy `startRollout`. Returns a stable callback
 * that POSTs `/services/{id}/rollouts` and resolves to the created
 * rollout id; rejects if the controller refuses (e.g. another rollout
 * is in progress or no pools are attached).
 */
export function useStartRollout() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (
      params: StartParams,
    ): Promise<RolloutOut> => {
      const body = {
        scope: params.scope,
        pool_id: params.pool_id ?? null,
        strategy: params.strategy ?? DEFAULT_STRATEGY,
      };
      return api.post<RolloutOut>(
        `/services/${params.service_id}/rollouts`,
        body,
      );
    },
    onSuccess: (row, params) => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_KEY });
      queryClient.invalidateQueries({
        queryKey: [...SERVICE_HISTORY_PREFIX, params.service_id],
      });
      // Service rollouts promote draft -> live and may flip a draft
      // service to active; bust the services cache so the tree row
      // reflects the new lifecycle_state immediately.
      queryClient.invalidateQueries({ queryKey: ["services"] });
      void params.on_started?.(row);
    },
  });
  return useCallback(
    async (params: StartParams): Promise<string> => {
      const row = await mutation.mutateAsync(params);
      return String(row.id);
    },
    [mutation],
  );
}

/** Hook variant of the legacy `abortRollout`. */
export function useAbortRollout() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (rolloutId: number) =>
      api.post<RolloutOut>(`/rollouts/${rolloutId}/abort`),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_KEY });
      queryClient.invalidateQueries({ queryKey: GLOBAL_HISTORY_KEY });
      if (row?.service_id !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [...SERVICE_HISTORY_PREFIX, row.service_id],
        });
      }
    },
  });
  return useCallback(
    async (rolloutId: number) => {
      await mutation.mutateAsync(rolloutId);
    },
    [mutation],
  );
}

// ---- estimation helpers (UI-only) ---------------------------------------

const POOL_PHASE_BASE_SECONDS: Record<
  Exclude<RolloutPoolPhase, "queued" | "complete" | "failed">,
  number
> = {
  preflight: 5,
  provisioning: 180,
  "health-grace": 30,
  flipping: 5,
  draining: 30,
  teardown: 10,
};

export function estimatePoolSeconds(
  replicas: number,
  strategy: BlueGreenStrategy,
): number {
  return (
    POOL_PHASE_BASE_SECONDS.preflight +
    POOL_PHASE_BASE_SECONDS.provisioning *
      Math.max(0.25, strategy.surge_pct / 100) *
      Math.min(1, Math.max(0.5, replicas / 4)) +
    strategy.health_check_grace_seconds +
    POOL_PHASE_BASE_SECONDS.flipping +
    strategy.drain_seconds +
    POOL_PHASE_BASE_SECONDS.teardown
  );
}

export interface PoolPlanInput {
  pool_id: number;
  pool_name: string;
  replicas: number;
  /**
   * Spec we plan to roll out. The controller derives the actual target
   * from `services.workload_draft` at start time; this field stays on
   * the input shape so the WebUI can render the planned diff in the
   * RolloutModal preview without hitting the network again.
   */
  target: WorkloadSpec;
}
